import type { ComponentProps, ReactNode } from "react";
import { isValidElement, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import { Mermaid } from "./Mermaid";

/**
 * Render de markdown compartilhado pelo chat (Message) E pela página pública de
 * conversa compartilhada (SharedConversationPage) - antes o share mostrava texto
 * cru (sem formatação, sem mermaid, sem realce). Fonte única pra os dois ficarem
 * sempre iguais.
 *
 * Realce de sintaxe (highlight.js via rehype-highlight) nos blocos ```lang.
 * `mermaid` fica de fora (vira diagrama, não código). `detect` desligado: só
 * realça quando a linguagem foi declarada (evita falsos positivos feios).
 */
const rehypePlugins: import("react-markdown").Options["rehypePlugins"] = [
  [rehypeHighlight, { detect: false, plainText: ["mermaid", "txt", "text"] }],
];
const remarkPlugins: import("react-markdown").Options["remarkPlugins"] = [remarkGfm];

/** Extrai o texto de dentro de um bloco de código (children do <code>). */
function codeText(node: ReactNode): string {
  if (typeof node === "string") return node;
  if (Array.isArray(node)) return node.map(codeText).join("");
  if (isValidElement(node)) {
    return codeText((node.props as { children?: ReactNode }).children);
  }
  return "";
}

/** Componentes de markdown (tabela com scroll, código com copiar, mermaid). */
const markdownComponents = {
  table: ({ children, ...props }: ComponentProps<"table">) => (
    <div className="table-wrap">
      <table {...props}>{children}</table>
    </div>
  ),
  pre: ({ children }: { children?: ReactNode }) => {
    // detecta ```mermaid → renderiza diagrama em vez de bloco de código
    const child = Array.isArray(children) ? children[0] : children;
    const className =
      isValidElement(child) && typeof (child.props as { className?: string }).className === "string"
        ? (child.props as { className?: string }).className ?? ""
        : "";
    if (/language-mermaid/.test(className)) {
      const chart = codeText((child as { props: { children?: ReactNode } }).props.children);
      return <Mermaid chart={chart} />;
    }
    return <CodeBlock>{children}</CodeBlock>;
  },
};

/** Renderiza markdown (GFM + realce + mermaid). Envolva num `.markdown` no chamador. */
export function MarkdownView({ content }: { content: string }) {
  return (
    <ReactMarkdown remarkPlugins={remarkPlugins} rehypePlugins={rehypePlugins} components={markdownComponents}>
      {content}
    </ReactMarkdown>
  );
}

/**
 * Bloco de código com botão de copiar no canto. Lê o texto direto do <pre>
 * renderizado (innerText), o que cobre qualquer realce/aninhamento.
 */
export function CodeBlock({ children }: { children?: ReactNode }) {
  const preRef = useRef<HTMLPreElement>(null);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    const text = preRef.current?.innerText ?? "";
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        /* noop */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="code-block">
      <button type="button" className="code-copy" onClick={copy} aria-label="Copiar código" title="Copiar código">
        {copied ? (
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M3 8.5l3 3 7-7" />
          </svg>
        ) : (
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <rect x="4" y="4" width="9" height="10" rx="1.5" />
            <path d="M3 11V3a1 1 0 0 1 1-1h8" />
          </svg>
        )}
        {copied ? "Copiado" : "Copiar"}
      </button>
      <pre ref={preRef}>{children}</pre>
    </div>
  );
}
